// Shared presentational window frame for the realtime dashboards (event + account).
import {ReactNode, useEffect, useState} from 'react';
import * as css from './WindowFrame.css';
import MobilePopup from '../../../../component/newPopup/mobilePopup/MobilePopup';
import useShow from '../../../../hooks/useShow';
import {Else, If, Then} from 'react-if';
import useMobile from '../../../../hooks/useMobile';
import WindowHeader from './WindowHeader';

export type WindowHeaderData = {
    title?: ReactNode;
    start?: ReactNode;
    end?: ReactNode;
    openLayout?: boolean;
    link?: {onClick?: () => void, text: string, href?: string};
    explainComp?: ReactNode;
    explainTitle?: string;
    explainDataCy?: string;
    howArrivedComp?: ReactNode;
    howArrivedTitle?: string;
    helper?: ReactNode;
    helperTitle?: string;
    helperDataCy?: string;
};

export type WindowFrameProps = {
    gridArea: string;
    height?: string;
    className?: string;
    dataCy?: string;
    // 'event' = floating icon header + event box; 'account' = title/actions header row + padded body; 'plain' = neutral frame, no header.
    variant?: 'event' | 'account' | 'plain';
    header?: WindowHeaderData;
    children?: ReactNode;
};

const EXPLAIN_COMPONENT = 'explainComponent';
const HOW_ARRIVED_COMPONENT = 'howArrivedComponent';

export default function WindowFrame({gridArea, height = null, className, dataCy, variant = 'event',
    header = {}, children = null}: WindowFrameProps) {
    const [openComponent, setOpenComponent] = useState('');
    const show = useShow();
    const isMobile = useMobile();
    const {explainComp, howArrivedComp, howArrivedTitle, helper} = header;
    const isOnArrived = openComponent === HOW_ARRIVED_COMPONENT;
    const isOnExplain = openComponent === EXPLAIN_COMPONENT;

    useEffect(() => {
        if(isMobile) {
            if(openComponent) { show.open(); }
            else { show.close(); }
        }
    }, [openComponent]);

    return (
        <css.windowWrapper className={className} gridArea={gridArea} height={height} $variant={variant} data-cy={dataCy}>
            {variant !== 'plain' && <WindowHeader variant={variant} header={header}
                onOpenExplain={() => setOpenComponent(EXPLAIN_COMPONENT)}
                onOpenArrived={() => setOpenComponent(HOW_ARRIVED_COMPONENT)}/>}
            {children}
            {!helper && (explainComp || howArrivedComp) && <If condition={isMobile}>
                <Then>
                    {show?.isOpen && <MobilePopup title={isOnArrived ? howArrivedTitle : ''} showClass={show?.showClass} close={() => setOpenComponent('')} dataCyName="realtimeWindowExplanation" list={undefined} forwardRef={undefined} TopComponent={null}>
                        <css.explainContainer isOpen={!!openComponent}>
                            {openComponent && <>
                                <css.explainWindowContent>
                                    {isOnExplain ? <>
                                        <css.questionIconMobile className="free-color">?</css.questionIconMobile>
                                        {explainComp}
                                    </>
                                        : isOnArrived ? howArrivedComp
                                            : ''}
                                </css.explainWindowContent>
                            </>}
                        </css.explainContainer>
                    </MobilePopup>}
                </Then>
                <Else>
                    <css.explainContainer isOpen={!!openComponent}>
                        <css.explainWindowTitle>
                            <css.backArrow onClick={() => setOpenComponent('')}>→</css.backArrow>
                            {isOnArrived && howArrivedTitle}
                        </css.explainWindowTitle>
                        <css.explainWindowContent>
                            {isOnExplain ? explainComp : ''}
                            {isOnArrived ? howArrivedComp : ''}
                        </css.explainWindowContent>
                    </css.explainContainer>
                </Else>
            </If>}
        </css.windowWrapper>
    );
}
